import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Loader2 } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

/** Settings → Privacy: the members YOU have blocked, with unblock. People who blocked you
 * are never listed. */
export function BlockedMembersCard() {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/blocks", { params: { limit: 100 } })
      .then(({ data }) => setRows(data.items || []))
      .catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const unblock = async (row) => {
    setBusyId(row.user_id);
    try {
      await api.delete(`/blocks/${row.user_id}`);
      toast.success(`${(row.member && row.member.display_name) || "Member"} unblocked`);
      load();
    } catch (e) {
      toast.error("Could not unblock right now");
    } finally { setBusyId(null); }
  };

  return (
    <Card className="p-6" data-testid="account-blocked-members-card">
      <h2 className="text-base font-[640] text-foreground md:text-lg">Blocked members</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        People you&apos;ve blocked can&apos;t message, connect with, follow or interact with
        you, and you won&apos;t see their personal posts. Your history is kept.
      </p>
      <div className="mt-4">
        {rows === null ? (
          <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="blocked-members-empty">
            You haven&apos;t blocked anyone.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="blocked-members-list">
            {rows.map((row) => {
              const m = row.member || {};
              return (
                <li key={row.user_id} className="flex items-center gap-3 py-3" data-testid={`blocked-member-${row.user_id}`}>
                  <Avatar className="h-9 w-9">
                    {m.headshot_url && <AvatarImage src={fileUrl(m.headshot_url)} alt="" />}
                    <AvatarFallback className="bg-secondary text-xs">
                      {(m.display_name || "?")[0]}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1 truncate text-sm font-[560] text-foreground">
                    {m.display_name || "Member"}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busyId === row.user_id}
                    onClick={() => unblock(row)}
                    data-testid={`unblock-${row.user_id}`}
                  >
                    {busyId === row.user_id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <><Ban className="mr-1.5 h-4 w-4" aria-hidden="true" /> Unblock</>}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
